import { ApplicationDependencyService,
    ApplicationGroupingService,
    ApplicationServerService,
    DatabaseApplicationService,
} from '@amzn/awscat-portfolio-assessment-service-client';
import { AppDependency, ApplicationGroup, ApplicationToServer, DatabaseApplication, GeneralDependencyType } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Checkbox, Grid, NonCancelableCustomEvent, Select, SelectProps } from '@amzn/awsui-components-react';
import { useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { deepCopy } from '../../../common/util';
import ConfirmationModal from '../../common/ConfirmationModal';
import {
    changeAppDatabaseMappingStatus,
    changeAppServerMappingStatus,
    changeDependencyStatus,
    countDependencies,
    decideDetailedMappingStatus,
    dependencyTypeMapping,
    findApplicationDatabaseMapping,
    findApplicationServerMapping,
    sortApplications,
    statusDiff,
} from './ApplicationGroupHelper';
import { APP_SORT_OPTIONS,AppDatabaseMappingToUpdate,AppServerMappingToUpdate, appSortSelectOptions, DEPENDENCY_STATUS, STATUS_IN_GROUPING } from './constants';

type Props = {
    onDismiss: () => void;
    loadData: () => void;
    groups: ApplicationGroup[];
    dependencies: AppDependency[];
    appServerMappings: ApplicationToServer[];
    appDatabaseMappings: DatabaseApplication[];
} & FlashContextInterface;

export type RemoveApplicationDetail = {
    id: string;
    checked: boolean;
    numServerDependencies: number;
    numDBDependencies: number;
};

type DependencyToManage = {
    serverMappingDependencies: AppDependency[];
    databaseMappingDependencies: AppDependency[];
    serverCommunicationDependencies: AppDependency[];
};

type MappingsToManage = {
    appServer: {[key: string]: {[key: string]: boolean}};
    appDatabase: {[key: string]: {[key: string]: boolean}};
};

const applicationGroupingService = new ApplicationGroupingService();
const applicationDependencyService = new ApplicationDependencyService();
const applicationServerService = new ApplicationServerService();
const databaseApplicationService = new DatabaseApplicationService();

function RemoveApplicationModal( { onDismiss, loadData, flash, groups, dependencies, appServerMappings, appDatabaseMappings }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId || '';
    
    const [ loading, setLoading ] = useState(false);
    const [ selectedGroup, setSelectedGroup ] = useState<SelectProps.Option | null>(null);
    const [ selectedGroupDetail, setSelectedGroupDetail ] = useState<ApplicationGroup | null>(null);
    const [ appSortOption, setAppSortOption ] = useState<SelectProps.Option>(appSortSelectOptions[0]);
    const [ applicationsInGroup, setApplicationsInGroup ] = useState<RemoveApplicationDetail[]>([]);
    const [ invalidMapping, setInvalidMapping ] = useState<boolean>(false);
    const [ dependenciesToManage, setDependenciesToManage ] = useState<DependencyToManage>(
        {serverMappingDependencies: [], databaseMappingDependencies: [], serverCommunicationDependencies: []}
    );
    const [ mappingsToManage, setMappingsToManage ] = useState<MappingsToManage>({appServer: {}, appDatabase: {}});
    const [ selectedAppSelectedAll, setSelectedAppSelectedAll ] = useState(true);
    const [ dependentAppSelectedAll, setDependentAppSelectedAll ] = useState(true);

    const isMounted = useIsMounted();

    const onConfirm = (stage: 'confirm' | 'review') => async () => {
        setLoading(true);
        let isInvalid = false;
        try {
            const { appServer, appDatabase } = mappingsToManage;
            const appMappingIds: {srcAppId: string; destAppId: string;} []= [];
            const otherDependenciesToUpdate: GeneralDependencyType[] = [];
            const appServerMappingsToUpdate: AppServerMappingToUpdate[] = [];
            const appDatabaseMappingsToUpdate: AppDatabaseMappingToUpdate[] = [];

            const selectedApps = applicationsInGroup.filter(app => app.checked);
            const unselectedApps = applicationsInGroup.filter(app => !app.checked);
            // Step 1: disable all dependencies of resourceType user-import and server-communications between a selected and an unselected app
            // The time complexity is n^2, we need to optimize this on the server size
            selectedApps.forEach(selectedApp => {
                unselectedApps.forEach(unselectedApp => {
                    otherDependenciesToUpdate.push({
                        srcAppId: selectedApp.id,
                        destAppId: unselectedApp.id,
                        resourceType: 'user-import'
                    }, {
                        srcAppId: selectedApp.id,
                        destAppId: unselectedApp.id,
                        resourceType: 'server-communications-v0',

                    }, {
                        srcAppId: unselectedApp.id,
                        destAppId: selectedApp.id,
                        resourceType: 'user-import'
                    }, {
                        srcAppId: unselectedApp.id,
                        destAppId: selectedApp.id,
                        resourceType: 'server-communications-v0',

                    });
                    appMappingIds.push({
                        srcAppId: selectedApp.id,
                        destAppId: unselectedApp.id
                    });
                });
            });

            appMappingIds.forEach(appIds => {
                const { srcAppId, destAppId } = appIds;
                // Step 2: remove all server mapping dependencies
                Object.keys(appServer).forEach(serverId => {
                    if (appServer[serverId][srcAppId] && appServer[serverId][destAppId]) {
                        isInvalid = true;
                        return;
                    }

                    const srcAppServerMapping = findApplicationServerMapping(appServerMappings, srcAppId, serverId);
                    const destAppServerMapping = findApplicationServerMapping(appServerMappings, destAppId, serverId);

                    // skip the pair of applications if any one of them doesn't have mapping with current server
                    if (!srcAppServerMapping || !destAppServerMapping) {
                        return;
                    } else {
                        if (statusDiff(srcAppServerMapping.statusInGrouping, appServer[serverId][srcAppId])) {
                            appServerMappingsToUpdate.push({
                                applicationId: srcAppId,
                                serverId,
                                status: decideDetailedMappingStatus(appServer[serverId][srcAppId], stage),
                            });
                        }
                        if (statusDiff(destAppServerMapping.statusInGrouping, appServer[serverId][destAppId])) {
                            appServerMappingsToUpdate.push({
                                applicationId: destAppId,
                                serverId,
                                status: decideDetailedMappingStatus(appServer[serverId][destAppId], stage),
                            });
                        }
                    }
                });
                // Step 3: remove all database mapping dependencies
                Object.keys(appDatabase).forEach(databaseId => {
                    if (appDatabase[databaseId][srcAppId] && appDatabase[databaseId][destAppId]) {
                        isInvalid = true;
                        return;
                    }

                    const srcAppDatabaseMapping = findApplicationDatabaseMapping(appDatabaseMappings, srcAppId, databaseId);
                    const destAppDatabaseMapping = findApplicationDatabaseMapping(appDatabaseMappings, destAppId, databaseId);

                    // skip the pair of applications if any one of them doesn't have mapping with current server
                    if (!srcAppDatabaseMapping || !destAppDatabaseMapping) {
                        return;
                    } else {
                        if (statusDiff(srcAppDatabaseMapping.statusInGrouping, appDatabase[databaseId][srcAppId])) {
                            appDatabaseMappingsToUpdate.push({
                                applicationId: srcAppId,
                                databaseId,
                                status: decideDetailedMappingStatus(appDatabase[databaseId][srcAppId], stage),
                            });
                        }
                        if (statusDiff(destAppDatabaseMapping.statusInGrouping, appDatabase[databaseId][destAppId])) {
                            appDatabaseMappingsToUpdate.push({
                                applicationId: destAppId,
                                databaseId,
                                status: decideDetailedMappingStatus(appDatabase[databaseId][destAppId], stage),
                            });
                        }
                    }
                });
            });

            if (isInvalid) {
                setInvalidMapping(true);
                return;
            } else {
                if (appServerMappingsToUpdate?.length > 0 || appDatabaseMappingsToUpdate?.length > 0) {
                    await Promise.all([
                        changeAppServerMappingStatus(appServerMappingsToUpdate, portfolioId, applicationServerService),
                        changeAppDatabaseMappingStatus(appDatabaseMappingsToUpdate, portfolioId, databaseApplicationService),
                    ]);
                }
                await changeDependencyStatus(DEPENDENCY_STATUS.INACTIVE, portfolioId, otherDependenciesToUpdate, applicationDependencyService);
                await applicationGroupingService.createGroups(portfolioId);
            }

        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_REMOVING_APPLICATION);
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
            if (!isInvalid) {
                onDismiss();
                loadData();
            }
        }
    };

    const selectGroupOptions = (): SelectProps.Options | undefined => {
        if (groups.length) {
            return groups.map(group => {
                return {
                    value: group.applicationGroupId,
                    label: group.name,
                };
            });
        }
        return undefined;
    };

    const onGroupChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>): void => {
        const groupToSelect = event.detail.selectedOption;
        setSelectedGroup(groupToSelect);
        const groupDetail = groups.find(group => group.applicationGroupId === groupToSelect.value) ?? null;
        setSelectedGroupDetail(groupDetail);
        const appDetails: RemoveApplicationDetail[] | undefined = groupDetail?.applicationIds.map(appId => ({
            id: appId,
            checked: false,
            numServerDependencies: countDependencies(dependencies, appId, 'applications-servers'),
            numDBDependencies: countDependencies(dependencies, appId, 'databases-applications'),
        }));
        if (appDetails) {
            setApplicationsInGroup(sortApplications(appDetails, appSortOption.value as APP_SORT_OPTIONS ?? APP_SORT_OPTIONS.D_TOTAL));
        }
    };

    const onSortOptionChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>): void => {
        const sortOption = event.detail.selectedOption;
        setAppSortOption(sortOption);
        setApplicationsInGroup(sortApplications(applicationsInGroup, sortOption.value as APP_SORT_OPTIONS));
    };

    const onSelectedAppChange = () => {
        const selectedApps = applicationsInGroup.filter(app => app.checked);
        const unselectedApps = applicationsInGroup.filter(app => !app.checked);

        setInvalidMapping(false);

        const selectedDependencies: AppDependency[] = [];
        selectedApps.forEach(selectedApp => {
            unselectedApps.forEach(unselectedApp => {
                selectedDependencies.push(...dependencies.filter(
                    dependency => dependency.srcAppId === selectedApp.id &&
                    dependency.destAppId === unselectedApp.id));
            });
        });
        const serverMappingDependencies = selectedDependencies.filter(dependency => 
            dependency.resourceType.startsWith('applications-servers') &&
            dependency.status === DEPENDENCY_STATUS.ACTIVE && !dependency.isSharedInfrastructure);
        const databaseMappingDependencies = selectedDependencies.filter(dependency => 
            dependency.resourceType.startsWith('databases-applications') &&
            dependency.status === DEPENDENCY_STATUS.ACTIVE && !dependency.isSharedInfrastructure);
        const serverCommunicationDependencies = selectedDependencies.filter(dependency => 
            dependency.resourceType.startsWith('server-communications') &&
            dependency.status === DEPENDENCY_STATUS.ACTIVE && !dependency.isSharedInfrastructure);

        setDependenciesToManage({databaseMappingDependencies, serverMappingDependencies, serverCommunicationDependencies});
        
        const groupAppServerMappings: {[key: string]: {[key: string]: boolean}} = {};
        const groupAppDatabaseMappings: {[key: string]: {[key: string]: boolean}} = {};

        serverMappingDependencies.forEach(dependency => {
            const { srcAppId, destAppId, resourceId } = dependency;
            const row: {[key: string]: boolean} = {};
            row[srcAppId] = findApplicationServerMapping(appServerMappings, srcAppId, resourceId)?.statusInGrouping === STATUS_IN_GROUPING.ACTIVE;
            row[destAppId] = findApplicationServerMapping(appServerMappings, destAppId, resourceId)?.statusInGrouping === STATUS_IN_GROUPING.ACTIVE;
            groupAppServerMappings[resourceId] = Object.assign(groupAppServerMappings[resourceId] || {}, row);
        });

        databaseMappingDependencies.forEach(dependency => {
            const { srcAppId, destAppId, resourceId } = dependency;
            const row: {[key: string]: boolean} = {};
            row[srcAppId] = findApplicationDatabaseMapping(appDatabaseMappings, srcAppId, resourceId)?.statusInGrouping === STATUS_IN_GROUPING.ACTIVE;
            row[destAppId] = findApplicationDatabaseMapping(appDatabaseMappings, destAppId, resourceId)?.statusInGrouping === STATUS_IN_GROUPING.ACTIVE;
            groupAppDatabaseMappings[resourceId] = Object.assign(groupAppDatabaseMappings[resourceId] || {}, row);
        });

        setMappingsToManage({appDatabase: groupAppDatabaseMappings, appServer: groupAppServerMappings});
    };

    const onCheckUpdate = (appId: string, checked: boolean) => {
        const appToUpdate = applicationsInGroup.find(app => app.id === appId);
        if (appToUpdate) {
            appToUpdate.checked = checked;
            setApplicationsInGroup([...applicationsInGroup]);
            onSelectedAppChange();
        }
    };

    const appList = (): JSX.Element => (
        <div>
            {applicationsInGroup.map(application => (
                <span key={application.id}>
                    <Checkbox checked={application.checked} onChange={({ detail }) => onCheckUpdate(application.id, detail.checked) }>
                        {`${application.id} (${application.numServerDependencies} ${constants.SERVERS}, ${application.numDBDependencies}`
                            + ` ${constants.DATABASES})`}
                    </Checkbox>
                </span>
            ))}
        </div>
    );

    const onMappingSelectionChange = (type: 'server' | 'database', resourceId: string, appId: string, checked: boolean): void => {
        if (type === 'server') {
            mappingsToManage.appServer[resourceId][appId] = checked;
        }
        else if (type === 'database') {
            mappingsToManage.appDatabase[resourceId][appId] = checked;
        }
        setMappingsToManage(deepCopy(mappingsToManage));

        const { serverMappingDependencies, databaseMappingDependencies } = dependenciesToManage;

        const selectedAppAllSelected = serverMappingDependencies.every(dependency => 
            mappingsToManage.appServer[dependency.resourceId][dependency.srcAppId]) &&
            databaseMappingDependencies.every(dependency => mappingsToManage.appDatabase[dependency.resourceId][dependency.srcAppId]);
        const dependentAppAllSelected = serverMappingDependencies.every(dependency => 
            mappingsToManage.appServer[dependency.resourceId][dependency.destAppId]) &&
            databaseMappingDependencies.every(dependency => mappingsToManage.appDatabase[dependency.resourceId][dependency.destAppId]);

        setSelectedAppSelectedAll(selectedAppAllSelected);
        setDependentAppSelectedAll(dependentAppAllSelected);
    };

    const selectAllDependencies = (appType: 'selected' | 'dependent', checked: boolean): void => {
        dependenciesToManage.serverMappingDependencies.forEach(dependency => {
            if (appType === 'selected') {
                mappingsToManage.appServer[dependency.resourceId][dependency.srcAppId] = checked;
            } else {
                mappingsToManage.appServer[dependency.resourceId][dependency.destAppId] = checked;
            }
        });

        dependenciesToManage.databaseMappingDependencies.forEach(dependency => {
            if (appType === 'selected') {
                mappingsToManage.appDatabase[dependency.resourceId][dependency.srcAppId] = checked;
            } else {
                mappingsToManage.appDatabase[dependency.resourceId][dependency.destAppId] = checked;
            }
        });

        if (appType === 'selected') {
            setSelectedAppSelectedAll(checked);
        } else {
            setDependentAppSelectedAll(checked);
        }

        setMappingsToManage(deepCopy(mappingsToManage));
    };

    const applicationSelection: JSX.Element | null = selectedGroup && (
        <Grid gridDefinition={[{colspan: 3}, {colspan: 9}]}>
            <Box className='app-dependency-modal-scroll'>
                <p>{constants.SELECT_APP_TO_REMOVE}</p>
                <b>{constants.SORT_BY}</b>
                <Select options={appSortSelectOptions} selectedOption={appSortOption} onChange={onSortOptionChange} />
                {appList()}
            </Box>
            <Box className='app-dependency-modal-scroll'>
                {invalidMapping ? (
                    <Alert
                        type='warning'
                        header={constants.ALERT_SELECT_ONE_MAPPING}
                    />
                ) : null}
                <p>{constants.REMOVE_APP_SELECTION_MESSAGE}</p>
                <table className='data-table'>
                    <thead>
                        <tr>
                            <th>{constants.RESOURCE_TYPE}</th>
                            <th>{constants.RESOURCE_ID}</th>
                            <th>
                                <Checkbox
                                    checked={selectedAppSelectedAll}
                                    onChange={({ detail }) => selectAllDependencies('selected', detail.checked)}
                                >
                                    {constants.SELECTED_APP}
                                </Checkbox>
                            </th>
                            <th>
                                <Checkbox
                                    checked={dependentAppSelectedAll}
                                    onChange={({ detail }) => selectAllDependencies('dependent', detail.checked)}
                                >
                                    {constants.DEPENDENT_APP}
                                </Checkbox>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {dependenciesToManage.serverMappingDependencies.map(dependency => (
                            <tr key={dependency.applicationDependencyId}>
                                <td>{dependencyTypeMapping[dependency.resourceType] || constants.NA}</td>
                                <td>{dependency.resourceId}</td>
                                <td>
                                    <Checkbox
                                        checked={mappingsToManage.appServer[dependency.resourceId][dependency.srcAppId]}
                                        onChange={
                                            ({ detail }) =>
                                                onMappingSelectionChange('server', dependency.resourceId, dependency.srcAppId, detail.checked)
                                        }
                                    >
                                        {dependency.srcAppId}
                                    </Checkbox>
                                </td>
                                <td>
                                    <Checkbox
                                        checked={mappingsToManage.appServer[dependency.resourceId][dependency.destAppId]}
                                        onChange={
                                            ({ detail }) =>
                                                onMappingSelectionChange('server', dependency.resourceId, dependency.destAppId, detail.checked)
                                        }
                                    >
                                        {dependency.destAppId}
                                    </Checkbox>
                                </td>
                            </tr>
                        ))}
                        {dependenciesToManage.databaseMappingDependencies.map(dependency => (
                            <tr key={dependency.applicationDependencyId}>
                                <td>{dependencyTypeMapping[dependency.resourceType] || constants.NA}</td>
                                <td>{dependency.resourceId}</td>
                                <td>
                                    <Checkbox
                                        checked={mappingsToManage.appDatabase[dependency.resourceId][dependency.srcAppId]}
                                        onChange={
                                            ({ detail }) =>
                                                onMappingSelectionChange('database', dependency.resourceId, dependency.srcAppId, detail.checked)
                                        }
                                    >
                                        {dependency.srcAppId}
                                    </Checkbox>
                                </td>
                                <td>
                                    <Checkbox
                                        checked={mappingsToManage.appDatabase[dependency.resourceId][dependency.destAppId]}
                                        onChange={
                                            ({ detail }) =>
                                                onMappingSelectionChange('database', dependency.resourceId, dependency.destAppId, detail.checked)
                                        }
                                    >
                                        {dependency.destAppId}
                                    </Checkbox>
                                </td>
                            </tr>
                        ))}
                        {dependenciesToManage.serverCommunicationDependencies.map(dependency => (
                            <tr key={dependency.applicationDependencyId}>
                                <td>{dependencyTypeMapping[dependency.resourceType] || constants.NA}</td>
                                <td>{dependency.resourceId}</td>
                                <td><Checkbox checked disabled>{dependency.srcAppId}</Checkbox></td>
                                <td><Checkbox checked disabled>{dependency.destAppId}</Checkbox></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Grid>
    );

    return (
        <ConfirmationModal
            visible
            header={constants.REMOVE_APPLICATONS_FROM_GROUP}
            confirmButton={constants.REMOVE_APPLICATONS_FROM_GROUP_BUTTON_TEXT}
            onConfirm={onConfirm('confirm')}
            secondaryButton={constants.MARK_FOR_REVIEW_BUTTON_TEXT}
            onSecondaryConfirm={onConfirm('review')}
            confirmLoading={loading}
            size='max'
            onDismiss={onDismiss}
        >
            <Box className='awscatui-min-height-20rem'>
                <Select options={selectGroupOptions()} selectedOption={selectedGroup} onChange={onGroupChange} />
                {selectedGroupDetail?.locked ? (
                    <Alert
                        type='info'
                        header={constants.ALERT_SELECTED_GROUP_LOCKED}
                    />
                ) : null}
                {applicationSelection}
            </Box>
        </ConfirmationModal>
    );
}

export default withFlashContext(RemoveApplicationModal);
